import type { ChangeEvent, FC } from 'react';
import { AnimateHeight } from '@components/AnimateHeight';
import InputText from '@components/InputText';
import { Button } from '@components/ui/button';
import { resolveSchema } from '@root/helpers/schema';
import type { FormError, JSONSchema, JSONSchemaProperties } from '@root/helpers/schema/types';
import type { MakeComponent as MakeComponentType } from '@src/mapChildren';
import { cn } from 'cn';
import { cloneDeep, omit, sortBy } from 'lodash-es';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

import { canAddChild, getNumberingLabel } from './tree-operations';
import type { ScheduleItemValue, SchedulePath } from './types';

export interface ScheduleItemRowProps {
  item: ScheduleItemValue;
  path: SchedulePath;
  siblingsCount: number;
  itemLabel: string;
  isExpanded: (path: SchedulePath) => boolean;
  onToggleExpand: (path: SchedulePath) => void;
  onUpdate: (path: SchedulePath, patch: Partial<ScheduleItemValue>) => void;
  onAddChild: (path: SchedulePath) => void;
  onRemove: (path: SchedulePath) => void;
  onMoveUp: (path: SchedulePath) => void;
  onMoveDown: (path: SchedulePath) => void;
  itemSchema: JSONSchemaProperties;
  rowOwnKey: string;
  onFieldChange?: (...args: any[]) => void;
  MakeComponent: typeof MakeComponentType;
  validationErrors?: FormError[] | null;
  hideErrors?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}

const BASIS_OTHER = 'Other';

const clearedToUndefined = (value: string): string | undefined => (value === '' ? undefined : value);

export const ScheduleItemRow: FC<ScheduleItemRowProps> = ({
  item,
  path,
  siblingsCount,
  itemLabel,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onAddChild,
  onRemove,
  onMoveUp,
  onMoveDown,
  itemSchema,
  rowOwnKey,
  onFieldChange,
  MakeComponent,
  validationErrors,
  hideErrors,
  isReadOnly = false,
  isDisabled = false,
}) => {
  const index = path[path.length - 1];
  const level = path.length;
  const expanded = isExpanded(path);
  const numberingLabel = `${itemLabel} ${getNumberingLabel(path)}`;
  // Descendant rows render inside this row's element, so every field testid is path-scoped —
  // an unscoped `value_type` would match this row's select and all of its children's.
  const testId = `schedule-item:${path.join('.')}`;

  const panelSchema = (() => {
    const cloned = cloneDeep(itemSchema) as unknown as JSONSchema;
    resolveSchema(cloned, item as Record<string, unknown>);
    return { ...cloned, properties: omit(cloned.properties, ['children', 'title']) } as JSONSchema;
  })();

  type PanelField = { reactKey: string; itemKey: string; property: unknown; parentKey: string; parentSchema: unknown };

  const panelFields: PanelField[] = sortBy(
    Object.entries(panelSchema.properties ?? {}),
    ([, property]) => (property as { qid?: number }).qid,
  ).flatMap(([key, property]) => {
    const nested = property as { type?: string; properties?: Record<string, unknown> };
    const isPlainObject = nested?.type === 'object' && nested.properties && !('ui:component' in nested);

    if (!isPlainObject) {
      return [{ reactKey: key, itemKey: key, property, parentKey: rowOwnKey, parentSchema: panelSchema }];
    }

    return Object.entries(nested.properties ?? {}).map(([childKey, childProperty]) => ({
      reactKey: `${key}.${childKey}`,
      itemKey: childKey,
      property: childProperty,
      parentKey: `${rowOwnKey}.${key}`,
      parentSchema: nested,
    }));
  });

  const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onUpdate(path, { title: clearedToUndefined(event.target.value) });

  const handleFieldChange = (value: unknown, name?: string) => {
    if (name === `${rowOwnKey}.value_type`) {
      return onUpdate(path, {
        value_type: value as ScheduleItemValue['value_type'],
        value_amount: undefined,
        value_percentage: undefined,
        value_timeframe: undefined,
        value_type_other: undefined,
      });
    }

    if (name === `${rowOwnKey}.basis`) {
      const selected = (value ?? []) as string[];

      return onUpdate(path, {
        basis: selected.length ? selected : undefined,
        basis_other: selected.includes(BASIS_OTHER) ? item.basis_other : undefined,
      });
    }

    return onFieldChange?.(value, name);
  };

  const titleErrors = validationErrors
    ?.filter((error) => error.ownKey === `${rowOwnKey}.title`)
    .map((error) => error.message)
    .filter((message): message is string => Boolean(message));

  return (
    <div className={cn('mt-3', level > 1 && 'ml-4 border-l-2 border-gray-300 pl-4')} data-testid={testId}>
      <div
        className={cn(
          'flex items-end gap-2 border border-gray-200 bg-white p-3',
          expanded ? 'rounded-t-md' : 'rounded-md',
        )}
      >
        <button
          type="button"
          data-testid={`${testId}:toggle`}
          aria-label={expanded ? 'Collapse item' : 'Expand item'}
          aria-expanded={expanded}
          onClick={() => onToggleExpand(path)}
          className="mb-2.5 shrink-0 text-gray-500"
        >
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>

        <div className="flex-1">
          <InputText
            labelText={numberingLabel}
            name={`${path.join('-')}-title`}
            dataTestId={`${testId}:title`}
            errors={titleErrors}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            value={item.title ?? ''}
            onChange={handleTitleChange}
            placeholder="Title"
          />
        </div>

        {/* size-10 matches the title input's height (the shared size_md token is h-10), so the
            group lines up with it instead of sitting 2px short. */}
        {!isReadOnly && (
          <div className="flex shrink-0 items-center [&>button]:-ml-px [&>button]:size-10 [&>button]:rounded-none [&>button:first-child]:ml-0 [&>button:first-child]:rounded-l-md [&>button:last-child]:rounded-r-md">
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Move up"
              data-testid={`${testId}:move-up`}
              disabled={index === 0 || isDisabled}
              onClick={() => onMoveUp(path)}
            >
              <ArrowUp className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Move down"
              data-testid={`${testId}:move-down`}
              disabled={index === siblingsCount - 1 || isDisabled}
              onClick={() => onMoveDown(path)}
            >
              <ArrowDown className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={`Add a sub ${itemLabel.toLowerCase()}`}
              data-testid={`${testId}:add-child`}
              disabled={!canAddChild(path) || isDisabled}
              onClick={() => onAddChild(path)}
            >
              <Plus className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Remove"
              data-testid={`${testId}:remove`}
              disabled={isDisabled}
              onClick={() => onRemove(path)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        )}
      </div>

      <AnimateHeight isVisible={expanded} className={expanded ? 'overflow-visible' : 'overflow-hidden'}>
        <div className="grid grid-cols-3 gap-3 rounded-b-md border-x border-b border-gray-200 p-3">
          {panelFields.map(({ reactKey, itemKey, property, parentKey, parentSchema }) => (
            <MakeComponent
              key={reactKey}
              item={{ ...(property as object), key: itemKey } as never}
              parentKey={parentKey}
              parentSchema={parentSchema as never}
              onChange={handleFieldChange as never}
              validationErrors={validationErrors ?? null}
              hideErrors={hideErrors}
              isReadOnly={isReadOnly}
              isDisabled={isDisabled}
            />
          ))}
        </div>
      </AnimateHeight>

      {(item.children ?? []).map((child, childIndex) => (
        <ScheduleItemRow
          key={childIndex}
          item={child}
          path={[...path, childIndex]}
          siblingsCount={item.children?.length ?? 0}
          itemLabel={itemLabel}
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
          onUpdate={onUpdate}
          onAddChild={onAddChild}
          onRemove={onRemove}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          itemSchema={itemSchema}
          rowOwnKey={`${rowOwnKey}.children[${childIndex}]`}
          onFieldChange={onFieldChange}
          MakeComponent={MakeComponent}
          validationErrors={validationErrors}
          hideErrors={hideErrors}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
        />
      ))}
    </div>
  );
};
